import { useState, useCallback } from 'react';
import type {
  ToastVariant,
  ToastActionButton,
} from '@/components/ui/ToastNotification';

export interface ToastState {
  open: boolean;
  title: string;
  description: string;
  variant: ToastVariant;
  action?: ToastActionButton;
}

export interface UseToastNotificationReturn {
  /** Current toast state */
  toast: ToastState;
  /** Show a toast notification */
  showToast: (params: {
    title: string;
    description: string;
    variant?: ToastVariant;
    action?: ToastActionButton;
  }) => void;
  /** Hide the current toast */
  hideToast: () => void;
}

const initialState: ToastState = {
  open: false,
  title: '',
  description: '',
  variant: 'success',
  action: undefined,
};

/**
 * Hook for managing toast notification state
 *
 * @example
 * ```tsx
 * const { toast, showToast, hideToast } = useToastNotification();
 *
 * // Show a success toast
 * showToast({
 *   title: 'Success!',
 *   description: 'Your changes have been saved.',
 *   variant: 'success',
 * });
 *
 * // Show an error toast
 * showToast({
 *   title: 'Error',
 *   description: 'Something went wrong.',
 *   variant: 'error',
 * });
 *
 * // Show a toast with an action button
 * showToast({
 *   title: 'Success!',
 *   description: 'Your changes have been saved.',
 *   variant: 'success',
 *   action: {
 *     label: 'View Details',
 *     onClick: () => navigate('/details'),
 *     variant: 'default',
 *   },
 * });
 * ```
 */
export function useToastNotification(): UseToastNotificationReturn {
  const [toast, setToast] = useState<ToastState>(initialState);

  const showToast = useCallback(
    (params: {
      title: string;
      description: string;
      variant?: ToastVariant;
      action?: ToastActionButton;
    }) => {
      setToast({
        open: true,
        title: params.title,
        description: params.description,
        variant: params.variant || 'success',
        action: params.action,
      });
    },
    []
  );

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    toast,
    showToast,
    hideToast,
  };
}

